/**
 * Implémentation Supabase de `ImportGateway`.
 *
 * Toute la logique d'import est dans `import.ts`, qui ne connaît pas la base.
 * Ce fichier ne fait que traduire : une méthode de la passerelle, une requête.
 * Il n'y a délibérément aucune règle métier ici — si une décision se prend dans
 * ce fichier, elle est au mauvais endroit.
 *
 * ## Les écritures passent par l'utilisateur, pas par le service_role
 *
 * L'import s'exécute dans l'interface d'administration, avec la session de
 * l'administrateur. Les politiques RLS des six tables autorisent le rôle
 * `admin` à écrire ; aucune clé de service n'est nécessaire, et donc aucune clé
 * de service ne traîne dans le navigateur.
 *
 * ## Les factures sont chargées par lots
 *
 * Un relevé cite jusqu'à 150 factures. `loadInvoicesByDigits` construit un
 * `OR` de motifs plutôt qu'une requête par facture : 22 fichiers font 22
 * requêtes, pas 3 300.
 */

import { db } from './db';
import type { ImportGateway, PaymentRow, RefundRow } from './import';
import { NUMERO_HISTORIQUE, type InvoiceRef } from './matching';

/** Nombre de motifs par requête, pour ne pas dépasser la longueur d'URL. */
const TAILLE_LOT = 50;

function decouper<T>(items: T[], taille: number): T[][] {
  const lots: T[][] = [];
  for (let i = 0; i < items.length; i += taille) lots.push(items.slice(i, i + taille));
  return lots;
}

interface LigneSolde {
  invoice_id: string;
  invoice_number: string;
  amount_cents: number;
  allocated_cents: number;
  /**
   * Ajoutée par la migration 20260903110922. Le `?? 0` n'est pas une
   * précaution de style : il permet au moteur de tourner sur une base où cette
   * migration n'est pas encore appliquée, sans se tromper d'un centime — sans
   * note de crédit, net et brut sont égaux.
   */
  credit_notes_cents?: number;
  status: string;
  /**
   * Ajoutée à la vue par `20260904221500`. Optionnelle pour la même raison que
   * ci-dessus : sur une base où la migration n'est pas encore appliquée, le
   * moteur perd la piste structurée mais garde toutes les autres, au lieu de
   * refuser l'import entier.
   */
  structured_communication?: string | null;
}

/*
  Les colonnes que le moteur lit d'une facture. Ecrites une fois : deux listes
  qui divergent donneraient `structuredDigits: undefined` sur un seul des deux
  chemins, et la piste la plus sûre du moteur s'éteindrait en silence.
*/
const CHAMPS_SOLDE =
  'invoice_id, invoice_number, amount_cents, allocated_cents, credit_notes_cents, '
  + 'status, structured_communication';

function versRef(l: LigneSolde): InvoiceRef {
  return {
    id: l.invoice_id,
    invoiceNumber: l.invoice_number,
    amountCents: l.amount_cents,
    allocatedCents: l.allocated_cents,
    creditNotesCents: l.credit_notes_cents ?? 0,
    status: l.status,
    structuredDigits: l.structured_communication ?? null,
  };
}

/**
 * Faux dès qu'une écriture a prouvé que `payments.free_text` n'existe pas.
 *
 * Sert à ne pas retenter la colonne à chaque lot d'un même import. Volontaire
 * qu'il ne soit pas persisté : au rechargement de la page, on retente, ce qui
 * fait que l'application de la migration est prise en compte sans redéploiement.
 */
let freeTextDisponible = true;

/**
 * Faux dès qu'une écriture a prouvé que `booking_date` n'existe pas encore
 * (migration `la_date_comptable_des_mouvements`). Même règle que pour
 * `free_text` : l'import ne casse pas si le code précède la migration ; il
 * écrit alors sans la date comptable, comme avant.
 */
let dateComptableDisponible = true;

/** L'erreur dit-elle que cette colonne n'existe pas ? */
function colonneAbsente(erreur: unknown, colonne: string): boolean {
  const e = erreur as { code?: string; message?: string };
  const message = e?.message ?? '';
  // `42703` est le code PostgreSQL, `PGRST204` celui que PostgREST rend quand
  // la colonne est absente de son cache de schéma. Les deux arrivent.
  const codeParlant = e?.code === '42703' || e?.code === 'PGRST204';
  return (codeParlant || /column .* does not exist|could not find the .* column/i.test(message))
    && message.includes(colonne);
}

export const importGatewaySupabase: ImportGateway = {
  async findImportByHash(sha256) {
    const { data, error } = await db
      .from('payment_imports')
      .select('id, filename, created_at')
      .eq('content_sha256', sha256)
      .maybeSingle();
    if (error) throw error;
    return data ? { id: data.id, filename: data.filename, createdAt: data.created_at } : null;
  },

  async createImport(row) {
    const { data, error } = await db
      .from('payment_imports')
      .insert({ ...row, problems: row.problems })
      .select('id')
      .single();
    if (error) throw error;
    return data.id;
  },

  async updateImportCounts(id, counts) {
    const { error } = await db.from('payment_imports').update(counts).eq('id', id);
    if (error) throw error;
  },

  async findKnownPayments(externalRefs, source = 'bank') {
    if (externalRefs.length === 0) return [];
    const resultats: { external_ref: string; id: string; allocated_cents: number }[] = [];
    for (const lot of decouper(externalRefs, 200)) {
      const { data, error } = await db
        .from('payments')
        .select('id, external_ref, allocated_cents')
        .eq('source', source)
        .in('external_ref', lot);
      if (error) throw error;
      resultats.push(...(data ?? []));

      /* Pour le relevé, une référence est aussi CONNUE quand une ligne de
         l'export CSV la porte comme `coda_ref` : c'est le même mouvement, déjà
         écrit. Sans cette seconde lecture, relire un relevé réuni le 27/09
         réécrirait ses 44 entrées — et la base le refuserait sur la première. */
      if (source === 'bank') {
        const { data: reliees, error: erreur } = await db
          .from('payments')
          .select('id, coda_ref, allocated_cents')
          .in('coda_ref', lot);
        if (erreur) throw erreur;
        for (const l of (reliees ?? []) as { id: string; coda_ref: string; allocated_cents: number }[]) {
          resultats.push({ external_ref: l.coda_ref, id: l.id, allocated_cents: l.allocated_cents });
        }
      }
    }
    return resultats;
  },

  async mouvementsCsvARelier(du, au) {
    const { data, error } = await db.rpc('mouvements_csv_a_relier', { p_du: du, p_au: au });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map((l) => ({
      sens: l.sens === 'sortie' ? 'sortie' as const : 'entree' as const,
      id: String(l.id),
      valueDate: String(l.value_date),
      amountCents: Number(l.amount_cents),
      counterpartyIban: (l.counterparty_iban as string | null) ?? null,
      structuredDigits: (l.structured_digits as string | null) ?? null,
      communicationRaw: (l.communication_raw as string | null) ?? null,
      allocatedCents: Number(l.allocated_cents ?? 0),
    }));
  },

  async relierAuCoda(liens) {
    if (liens.length === 0) return 0;
    const { data, error } = await db.rpc('relier_au_coda', { p_liens: liens });
    if (error) throw error;
    return Number(data ?? 0);
  },

  /**
   * Les remboursements déjà en base, parmi ceux proposés.
   *
   * Découpé par 200 comme `findKnownPayments` : au-delà, la longueur de l'URL
   * de PostgREST devient le facteur limitant, et un lot de 200 ne peut de toute
   * façon pas rendre plus de 200 lignes.
   */
  async findKnownRefunds(externalRefs, source = 'bank') {
    if (externalRefs.length === 0) return [];
    const resultats: {
      external_ref: string; id: string;
      counterparty_iban: string | null; counterparty_name: string | null; free_text: string | null;
    }[] = [];
    for (const lot of decouper(externalRefs, 200)) {
      const { data, error } = await db
        .from('refunds')
        /* La contrepartie et le texte libre viennent avec : c'est ce qui permet
           a une relecture de savoir ce qui MANQUE. Sans eux, completer une
           ligne reviendrait a l'ecraser a l'aveugle. */
        .select('id, external_ref, counterparty_iban, counterparty_name, free_text')
        .eq('source', source)
        .in('external_ref', lot);
      if (error) throw error;
      resultats.push(...((data ?? []) as unknown as typeof resultats));

      /* Même règle que pour les paiements : une sortie de l'export CSV reliée
         au relevé est connue par la référence du relevé. */
      if (source === 'bank') {
        const { data: reliees, error: erreur } = await db
          .from('refunds')
          .select('id, coda_ref, counterparty_iban, counterparty_name, free_text')
          .in('coda_ref', lot);
        if (erreur) throw erreur;
        for (const l of (reliees ?? []) as unknown as (typeof resultats[number] & { coda_ref: string })[]) {
          const { coda_ref: ref, ...reste } = l;
          resultats.push({ ...reste, external_ref: ref });
        }
      }
    }
    return resultats;
  },

  /**
   * Complète des remboursements déjà en base, un par un.
   *
   * ## Pourquoi une écriture par ligne
   *
   * Chaque ligne n'a pas les mêmes trous : l'une n'a pas de compte, l'autre pas
   * de nom, la troisième ni l'un ni l'autre. Un `upsert` groupé écrirait les
   * mêmes colonnes partout et **remplacerait par `null`** ce qui était déjà là.
   * Le plan a déjà décidé, champ par champ, ce qui manque ; il ne reste qu'à
   * l'écrire.
   *
   * ## Les garde-fous `is null`
   *
   * Chaque mise à jour ne porte que sur les colonnes que le plan a vues vides,
   * et le filtre les redemande vides au moment d'écrire. Entre la lecture et
   * l'écriture, quelqu'un peut avoir renseigné le compte à la main — et une
   * saisie humaine vaut mieux qu'une relecture de fichier.
   */
  async completeRefunds(rows) {
    let modifiees = 0;
    for (const lot of decouper(rows, 20)) {
      const resultats = await Promise.all(lot.map(async (r) => {
        const champs: Record<string, string> = {};
        let requete = db.from('refunds').update(champs).eq('id', r.id);
        if (r.counterparty_iban !== undefined) {
          champs.counterparty_iban = r.counterparty_iban;
          requete = requete.is('counterparty_iban', null);
        }
        if (r.counterparty_name !== undefined) {
          champs.counterparty_name = r.counterparty_name;
          requete = requete.is('counterparty_name', null);
        }
        if (r.free_text !== undefined) {
          champs.free_text = r.free_text;
          requete = requete.is('free_text', null);
        }
        if (Object.keys(champs).length === 0) return 0;
        const { data, error } = await requete.select('id');
        if (error) throw error;
        return (data ?? []).length;
      }));
      modifiees += resultats.reduce((a, b) => a + b, 0);
    }
    return modifiees;
  },

  /**
   * Recherche par référence : les 7 chiffres finaux, ou le numéro entier.
   *
   * Le tag ne numérote rien, donc on ne peut pas reconstruire le numéro complet
   * depuis une référence lue sur un relevé : on cherche sur le suffixe. D'où le
   * `like` en `%<chiffres>`, et non une égalité.
   *
   * ## Sauf pour les 178 factures du format historique
   *
   * `CDV-250530-00010` ne se termine PAS par ses sept derniers chiffres
   * `3000010` : le tiret les coupe en `0-00010`. Le `like` en suffixe ne rend
   * donc rien, même quand la référence a été correctement lue — et c'est ce
   * qui rendait ce format introuvable de bout en bout.
   *
   * Pour ceux-là, le numéro est entier et connu : on cherche l'ÉGALITÉ, ce qui
   * est à la fois exact et indexable.
   */
  async loadInvoicesByDigits(references) {
    if (references.length === 0) return [];
    const factures: InvoiceRef[] = [];
    for (const lot of decouper(references, TAILLE_LOT)) {
      const { data, error } = await db
        .from('v_invoice_balances')
        .select(CHAMPS_SOLDE)
        // Le joker de PostgREST est `*`, converti en `%` cote SQL. Ecrire `%`
        // directement fonctionne par accident selon l'encodage de l'URL ; `*`
        // est la forme documentee.
        .or(lot.map((r) => (NUMERO_HISTORIQUE.test(r)
          ? `invoice_number.eq.${r}`
          : `invoice_number.like.*${r}`)).join(','));
      if (error) throw error;
      factures.push(...((data ?? []) as unknown as LigneSolde[]).map(versRef));
    }
    return factures;
  },

  /**
   * Recherche par communication structurée.
   *
   * C'est la piste la plus sûre du moteur : une communication structurée est
   * **validée par la banque du parent à la saisie**, clé modulo 97 à l'appui.
   * Elle ne peut pas être mal recopiée — contrairement au numéro de facture,
   * saisi à la main dans un champ libre.
   *
   * La méthode rendait une liste vide tant que la colonne n'existait pas, et
   * c'était la bonne réponse : les 1 442 communications structurées déjà lues
   * appartiennent toutes à la garderie. Depuis `20260904215411` les factures
   * en portent une, préfixée `100` — un espace de noms que la grammaire de la
   * garderie ne peut pas occuper, le mois `00` n'existant pas.
   */
  async loadInvoicesByStructured(digits) {
    if (digits.length === 0) return [];
    const factures: InvoiceRef[] = [];
    for (const lot of decouper(digits, TAILLE_LOT)) {
      const { data, error } = await db
        .from('v_invoice_balances')
        .select(CHAMPS_SOLDE)
        .in('structured_communication', lot);
      if (error) throw error;
      factures.push(...((data ?? []) as unknown as LigneSolde[]).map(versRef));
    }
    return factures;
  },

  /**
   * Écrit les paiements, et sait se passer de `free_text`.
   *
   * La colonne est ajoutée par la migration 20260903111107. Tant qu'elle n'est
   * pas appliquée, l'insertion serait rejetée **en entier** par PostgREST — un
   * import de la semaine échouerait pour une colonne d'affichage. Le premier
   * refus de ce type fait donc réessayer sans elle et le retient.
   *
   * Ce n'est pas une élégance : c'est ce qui permet d'appliquer la migration
   * et de déployer le code dans l'ordre qu'on veut, sans fenêtre où l'import
   * est cassé.
   */
  /**
   * Combien de mouvements la base porte déjà, par forme, sur la période.
   *
   * Une fonction en base, et un objet JSON en réponse : PostgREST plafonne un
   * ensemble de lignes à mille, et une réponse tronquée en silence ferait
   * réécrire tous les mouvements du dernier tiers de l'année. Sur 2025 et 2026,
   * la réponse compte plus de dix mille formes distinctes.
   */
  async mouvementsConnus(du: string, au: string) {
    const { data, error } = await db.rpc('mouvements_bancaires_connus', { p_du: du, p_au: au });
    if (error) throw error;
    return (data ?? {}) as Record<string, number>;
  },

  async insertPayments(importId, rows: PaymentRow[]) {
    const idParRef = new Map<string, string>();

    for (const lot of decouper(rows, 200)) {
      const ligne = (r: PaymentRow) => {
        const { free_text, booking_date, ...reste } = r;
        return {
          ...reste,
          import_id: importId,
          ...(freeTextDisponible ? { free_text } : {}),
          ...(dateComptableDisponible ? { booking_date } : {}),
        };
      };

      let { data, error } = await db
        .from('payments')
        .insert(lot.map(ligne))
        .select('id, external_ref');

      // Une colonne absente à la fois, une seule fois chacune : les lots
      // suivants de cet import partent directement sans elle.
      for (let essai = 0; error && essai < 2; essai++) {
        if (freeTextDisponible && colonneAbsente(error, 'free_text')) freeTextDisponible = false;
        else if (dateComptableDisponible && colonneAbsente(error, 'booking_date')) dateComptableDisponible = false;
        else break;
        ({ data, error } = await db
          .from('payments')
          .insert(lot.map(ligne))
          .select('id, external_ref'));
      }
      if (error) throw error;
      for (const p of data ?? []) idParRef.set(p.external_ref, p.id);
    }
    return idParRef;
  },

  async insertAllocations(rows) {
    let total = 0;
    for (const lot of decouper(rows, 200)) {
      // Pas de `select()` : on n'a besoin que du compte, et les déclencheurs
      // de la base font le reste — allocated_cents recalculé, invariants
      // vérifiés. Une violation lève ici, elle n'est pas silencieuse.
      const { error, count } = await db
        .from('payment_allocations')
        .insert(lot, { count: 'exact' });
      if (error) throw error;
      total += count ?? lot.length;
    }
    return total;
  },

  async insertRefunds(importId, rows: RefundRow[]) {
    let total = 0;
    for (const r of rows) {
      const ecrire = () => db
        .from('refunds')
        .insert({
          source: r.source,
          external_ref: r.external_ref,
          import_id: importId,
          amount_cents: r.amount_cents,
          value_date: r.value_date,
          /* La date comptable de la banque : elle choisit l'exercice. */
          ...(dateComptableDisponible ? { booking_date: r.booking_date } : {}),
          communication_raw: r.communication_raw,
          /* La nature de la communication et ses douze chiffres : sans eux,
             l'ecran ne peut pas distinguer une demande de remboursement que
             NOUS avons emise d'une annotation manuelle. */
          communication_kind: r.communication_kind,
          structured_digits: r.structured_digits,
          /* Le libelle complet du releve. Les 1 758 virements sortants du
             fichier d'un an en ont un, et aucun n'etait ecrit : l'ecran n'avait
             donc que la communication tronquee a 53 caracteres. */
          free_text: r.free_text,
          invoice_id: r.invoice_id,
          legacy_invoice_ref: r.legacy_invoice_ref,
          possibly_truncated: r.possibly_truncated,
          /* Le bénéficiaire, pour pouvoir chercher une dépense par son compte. */
          counterparty_iban: r.counterparty_iban,
          counterparty_name: r.counterparty_name,
        })
        .select('id')
        .single();
      let { data, error } = await ecrire();
      if (error && dateComptableDisponible && colonneAbsente(error, 'booking_date')) {
        dateComptableDisponible = false;
        ({ data, error } = await ecrire());
      }
      if (error) throw error;
      if (!data) throw new Error(`le remboursement ${r.external_ref} n'a pas été relu après son écriture`);
      total++;

      /*
        Le classement hors perimetre, pose A L'ECRITURE.

        Le mouvement est ecrit, puis son montant entier est affecte a
        `out_of_scope` : il sort de la file sans jamais etre rattache a une
        famille. C'est la symetrie exacte de ce que fait l'entrant, et c'est ce
        qui permet de tout lire sans noyer l'ecran — 1 375 des 1 758 virements
        sortants du fichier d'un an sont dans ce cas.

        Il reste DEFAISABLE : un volontariat mal reconnu se rouvre d'un clic
        depuis la file, et redevient placable a la main.
      */
      if (r.out_of_scope) {
        const { error: erreurHors } = await db.from('refund_allocations').insert({
          refund_id: data.id,
          amount_cents: r.amount_cents,
          target: 'out_of_scope',
          method: 'manual',
          notes: `Reconnu a la forme : ${r.out_of_scope}`,
        });
        if (erreurHors) throw erreurHors;
      }

      // Les numéros de note de crédit sont conservés tels quels, même quand la
      // note n'existe pas dans cette base — c'est le cas de tout ce qui a été
      // traité dans le tableur pendant l'année sans vérification.
      if (r.creditNoteNumbers.length) {
        const { data: notes } = await db
          .from('credit_notes')
          .select('id, credit_note_number')
          .in('credit_note_number', r.creditNoteNumbers);
        const parNumero = new Map((notes ?? []).map((n) => [n.credit_note_number, n.id]));

        const { error: erreurNotes } = await db.from('refund_credit_notes').insert(
          r.creditNoteNumbers.map((numero) => ({
            refund_id: data.id,
            credit_note_number: numero,
            credit_note_id: parNumero.get(numero) ?? null,
          })),
        );
        if (erreurNotes) throw erreurNotes;
      }
    }
    return total;
  },
};
